/**
 * ChangeAuditLog
 * Shows a history of all data changes made by Scheduler Admins.
 * Tracks: instructor edits, credential add/delete, slot preference changes.
 * Visible only to Scheduler.Admin / Survey.Admin.
 */

import { useState, useEffect } from 'react';
import { schedulerApi, AuditEvent } from '../../services/scheduler-api';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginTop: '1.5rem',
} as const;

const TH = {
  padding: '10px 14px',
  textAlign: 'left' as const,
  color: 'var(--text-secondary)',
  fontWeight: '600' as const,
  fontSize: '12px',
  whiteSpace: 'nowrap' as const,
  borderBottom: '2px solid var(--card-border)',
};

const TD = {
  padding: '10px 14px',
  fontSize: '13px',
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--card-border)',
  verticalAlign: 'top' as const,
};

function fmtDate(iso: string): string {
  if (!iso) return '—';
  try {
    // Normalize: replace space separator, and add Z only if no timezone info present
    const s = iso.replace(' ', 'T');
    const normalized = (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) ? s : s + 'Z';
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const ACTION_COLORS: Record<string, string> = {
  UPDATE: '#60a5fa',
  CREATE: '#4ade80',
  DELETE: '#f87171',
  CLEAR:  '#fb923c',
};

function ChangeDiff({ before, after }: { before: string; after: string }) {
  let b: Record<string, unknown> = {};
  let a: Record<string, unknown> = {};
  try { b = JSON.parse(before); } catch { /* ignore */ }
  try { a = JSON.parse(after); } catch { /* ignore */ }

  const allKeys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  if (allKeys.length === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>—</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {allKeys.map(key => {
        const bVal = String(b[key] ?? '');
        const aVal = String(a[key] ?? '');
        const changed = bVal !== aVal;
        if (!changed && !bVal && !aVal) return null;
        return (
          <div key={key} style={{ fontSize: '11px', display: 'flex', gap: '4px', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-secondary)', minWidth: '80px' }}>{key}:</span>
            {!changed ? (
              <span style={{ color: 'var(--text-secondary)' }}>{bVal || '—'}</span>
            ) : (
              <>
                {bVal && <span style={{ color: '#f87171', textDecoration: 'line-through' }}>{bVal}</span>}
                {bVal && aVal && <span style={{ color: 'var(--text-secondary)' }}>→</span>}
                {aVal && <span style={{ color: '#4ade80' }}>{aVal}</span>}
              </>
            )}
          </div>
        );
      }).filter(Boolean)}
    </div>
  );
}

export function ChangeAuditLog({ alwaysOpen }: { alwaysOpen?: boolean } = {}) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (alwaysOpen && !hasFetched) {
      setHasFetched(true);
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alwaysOpen]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !hasFetched) {
      setHasFetched(true);
      load();
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await schedulerApi.getAuditLog();
      setEvents(data.events || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const entityTypes = Array.from(new Set(events.map(e => e.entity_type))).sort();

  const filtered = events.filter(e =>
    (!filter || e.admin_email.toLowerCase().includes(filter.toLowerCase()) ||
                 e.entity_row_key.includes(filter)) &&
    (!entityFilter || e.entity_type === entityFilter)
  );

  const showContent = alwaysOpen || expanded;

  return (
    <div style={{ background: 'var(--card-bg)', border: alwaysOpen ? 'none' : '1px solid var(--card-border)', borderRadius: alwaysOpen ? '0' : '12px', overflow: 'hidden' }}>
      {/* Clickable header — hidden in alwaysOpen mode */}
      {!alwaysOpen && (
        <button
          onClick={toggle}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>📋 Admin Change Log</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {hasFetched && !loading ? `${events.length} event${events.length !== 1 ? 's' : ''}` : 'History of all admin data changes'}
            </div>
          </div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '14px', flexShrink: 0, marginLeft: '0.5rem' }}>{expanded ? '▲' : '▼'}</span>
        </button>
      )}

      {/* Content */}
      {showContent && (
        <div style={{ borderTop: alwaysOpen ? 'none' : '1px solid var(--card-border)', padding: '1.25rem' }}>
          {loading && <p style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center', padding: '1rem 0' }}>Loading…</p>}
          {error && <p style={{ color: '#f87171', fontSize: '13px' }}>❌ {error}</p>}
          {!loading && !error && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <select
                  value={entityFilter}
                  onChange={e => setEntityFilter(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '12px' }}
                >
                  <option value="">All types</option>
                  {entityTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  placeholder="Filter by admin or row key…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '12px', flex: 1, minWidth: '140px' }}
                />
                <button
                  onClick={load}
                  style={{ fontSize: '11px', padding: '5px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--card-border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}
                >
                  ↻ Refresh
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={TH}>When</th>
                      <th style={TH}>Admin</th>
                      <th style={TH}>Action</th>
                      <th style={TH}>Entity</th>
                      <th style={TH}>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {events.length === 0 ? 'No changes recorded yet. Changes are logged when admins edit instructor data, credentials, or slot preferences.' : 'No events match your filter.'}
                        </td>
                      </tr>
                    ) : filtered.map((ev, i) => (
                      <tr key={ev.id} style={{ borderBottom: '1px solid var(--card-border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                        <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '12px' }}>{fmtDate(ev.timestamp)}</td>
                        <td style={{ ...TD, color: 'var(--text-primary)' }}>{ev.admin_email}</td>
                        <td style={TD}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '700',
                            background: `${ACTION_COLORS[ev.action] ?? '#94a3b8'}22`,
                            color: ACTION_COLORS[ev.action] ?? '#94a3b8',
                            border: `1px solid ${ACTION_COLORS[ev.action] ?? '#94a3b8'}44`,
                          }}>
                            {ev.action}
                          </span>
                        </td>
                        <td style={TD}>
                          <span style={{ fontWeight: '600' }}>{ev.entity_type}</span>
                          {ev.entity_row_key && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '11px', marginLeft: '5px' }}>
                              #{ev.entity_row_key}
                            </span>
                          )}
                        </td>
                        <td style={TD}>
                          <ChangeDiff before={ev.before} after={ev.after} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
